import { Popper } from '@mui/material';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';

import OverflowMenuIcon from '@sbu/assets.svg/src/overflow-menu-2.svg';
import { ClickAwayListener, useTheme } from '@sumologic/yama-ds-components';

import { EnabledContextActionFragment as ContextActionType } from 'core/graphql';
import {
  IocContent,
  IocContentProps,
} from 'shared/components/internal/IocContent';

import css from './ContextAction.module.scss';
import { ContextActionMenuItems } from './ContextActionMenuItems';
import { ContextData } from './template';
import { Match, useMatchedTypes } from './utils';

export type ContextActionProps = IocContentProps & {
  className?: any;
  contextActions: ContextActionType[];
  contextActionsClassName?: any;
  contextData?: ContextData;
  entityType?: string;
  eventTimestamp?: Date;
  hideDots?: boolean;
  recordField?: string;
  sumoLogicRegion?: string;
  value?: string;
  onMenuToggle?: (menuVisible: boolean) => void;
  onQuickFilter?: (value: string, negate?: boolean) => void;
};

export const ContextAction = ({
  children,
  className,
  contextActions = [],
  contextActionsClassName,
  contextData,
  entityType,
  eventTimestamp,
  hideDots,
  hoverMatch,
  matchers,
  onClick,
  recordField,
  sumoLogicRegion,
  value,
  onMenuToggle,
  onQuickFilter,
}: ContextActionProps) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuVisible = Boolean(menuAnchorEl);
  const { palette } = useTheme();

  const [matches, setMatches] = useState<Match[]>([]);
  const matchedTypes = useMatchedTypes({
    actions: contextActions,
    entityType,
    matches,
    recordField,
  });
  const hasActions = !!matchedTypes.length || !!matches.length;

  const handleMenuToggle = useCallback(() => {
    if (hasActions) {
      setMenuAnchorEl(buttonRef.current);
      onMenuToggle?.(true);
    }
  }, [hasActions, onMenuToggle]);

  return (
    <div
      className={classNames(className, css.contextActionWrapper, {
        [css.menuActive]: menuVisible,
        [css.hasActions]: hasActions,
      })}
      data-sumo-test={`context-action-menu-toggle-${
        hasActions ? 'enabled' : 'disabled'
      }`}
      onClick={handleMenuToggle}
    >
      <IocContent
        hoverMatch={hoverMatch}
        matchers={matchers}
        onClick={onClick}
        onMatchesChange={setMatches}
        value={value}
      >
        {children}
      </IocContent>
      {hasActions && (
        <span
          className={classNames(css.contextActions, contextActionsClassName)}
          data-sumo-test='context-action-actions'
        >
          <div ref={buttonRef}>
            {!hideDots && <OverflowMenuIcon className={css.dots} />}
          </div>
          <Popper
            anchorEl={menuAnchorEl}
            className={css.menu}
            open={menuVisible}
            placement='bottom-end'
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: palette.tokens.YamaBorderVariantLowcontrastColor,
            }}
          >
            <ClickAwayListener
              onClickAway={() => {
                setMenuAnchorEl(null);
                onMenuToggle?.(false);
              }}
            >
              <div>
                <ContextActionMenuItems
                  contextData={contextData}
                  eventTimestamp={eventTimestamp}
                  matchedContextActions={matchedTypes}
                  matches={matches}
                  recordField={recordField}
                  sumoLogicRegion={sumoLogicRegion}
                  value={value}
                  onQuickFilter={onQuickFilter}
                >
                  {children}
                </ContextActionMenuItems>
              </div>
            </ClickAwayListener>
          </Popper>
        </span>
      )}
    </div>
  );
};
